import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

import { useRealtimeFrame } from '../../shared/api/realtime';

const EVERY_MS = 3_000;

/**
 * A destination's "now" (retry pending, last tries, failing now) is read live by the API, so a
 * delivery moving anywhere re-reads what is on screen — throttled, not debounced: steady traffic
 * would keep pushing a debounced re-read away. Frames do not name the destination.
 */
export const useDestinationLiveUpdate = (keys: readonly QueryKey[]): void => {
  const queryClient = useQueryClient();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latest = useRef(keys);
  latest.current = keys;

  const refresh = () => {
    if (timer.current !== undefined) return;
    timer.current = setTimeout(() => {
      timer.current = undefined;
      for (const queryKey of latest.current) void queryClient.invalidateQueries({ queryKey });
    }, EVERY_MS);
  };

  useRealtimeFrame('flow_copy.updated', refresh);
  useRealtimeFrame('flow_copy.skipped', refresh);
  useRealtimeFrame('flow_run.updated', refresh);
  useRealtimeFrame('event.created', refresh);

  useEffect(() => () => clearTimeout(timer.current), []);
};
